import { useMemo } from 'react';

import { pdf } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

import useEnv from '@zenbusiness/application-commons-env';
import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import * as ROUTES from '#client/constants/routes';
import { useCustomForm } from '#client/contexts/CustomFormContext';

import * as Styled from './BusinessCostResults.style';
import PoweredByVelo from '../PoweredByVelo/PoweredByVelo';
import BusinessResultPDF from './components/BusinessResultPDF/BusinessResultPDF';
import ReportPanel from './components/ReportPanel/ReportPanel';
import UserProfilePanel from './components/UserProfilePanel/UserProfilePanel';

/**
 * Business Results component - displays personalized business ideas and detailed reports
 * Replaces ChoiceFlow after form submission to show generated business recommendations
 * @param {object} props - Component props
 * @param {object} props.data - Business results data containing ideas and user profile
 * @param {Function} [props.onComponentClick] - Callback for component actions (follows assistant-ui pattern)
 * @component
 */
function BusinessCostResults({ data, onComponentClick }) {
  const { DEPLOY_ENV } = useEnv('head-first');
  const { isLoading, setLoading, resetForm, clearAnswers } = useCustomForm();
  console.log('data', data);

  const costReport = useMemo(() => data?.costReport || {}, [data?.costReport]);

  const handleDownloadReport = async () => {
    if (isLoading) return;

    setLoading(true);

    try {
      // const selectedIdea = data.business_ideas.find((idea) => idea.id === selectedIdeaId);
      const blob = await pdf(
        <BusinessResultPDF environment={DEPLOY_ENV} report={costReport} />
      ).toBlob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `Business_Cost_Report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[BusinessCostResults] Download report error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOver = async () => {
    if (isLoading) return;

    setLoading(true);
    if (onComponentClick) {
      await resetForm();
      await clearAnswers();
      await onComponentClick({ type: 'business_cost_start_over' });
    }
    setLoading(false);
  };

  // Handle case where no ideas are provided
  if (!costReport) {
    return (
      <Styled.BusinessResultsRoot>
        <Box style={{ textAlign: 'center' }}>
          <Text>No cost estimates found</Text>
          <Text>
            We weren't able to generate cost estimates based on your responses. Please try again.
          </Text>
        </Box>
      </Styled.BusinessResultsRoot>
    );
  }

  return (
    <Styled.BusinessResultsRoot>
      <Styled.ReportHeader>
        <Text size="md" weight="medium">
          Your Business Cost Estimator
        </Text>
      </Styled.ReportHeader>
      <Styled.ReportContent>
        <UserProfilePanel
          loading={isLoading}
          userProfile={costReport.userInput}
          onStartOver={handleStartOver}
        />
        <ReportPanel
          loading={isLoading}
          report={costReport}
          onDownloadReport={handleDownloadReport}
        />
      </Styled.ReportContent>
      <PoweredByVelo url={ROUTES.website(DEPLOY_ENV)} />
    </Styled.BusinessResultsRoot>
  );
}

BusinessCostResults.propTypes = {
  data: PropTypes.object.isRequired,
  onComponentClick: PropTypes.func
};

export default BusinessCostResults;
